import React from 'react';
import { Dialog, DialogTitle, DialogContent, Typography, Box, Chip, List, ListItem, ListItemIcon, ListItemText, Divider, Button } from '@mui/material';
import { CheckCircle, Cancel, OpenInNew } from '@mui/icons-material';

const currencyOptions = {
  USD: '$', EUR: '€', GBP: '£', AUD: 'A$', CAD: 'C$', JPY: '¥', ZAR: 'R'
};

function DetailsModal({ open, onClose, listing, currency, amenitiesList }) {
  const currencySymbol = currencyOptions[currency] || '$';
  
  // Map amenity IDs to amenity objects
  const amenityMap = Object.fromEntries((amenitiesList || []).map(a => [a.id, a]));
  const availableAmenities = (listing?.amenities || []).map(id => amenityMap[id]).filter(Boolean);
  const availableIds = new Set((listing?.amenities || []));
  
  // Group all amenities by category
  const groupedAmenities = (amenitiesList || []).reduce((acc, a) => {
    acc[a.category] = acc[a.category] || [];
    acc[a.category].push(a);
    return acc;
  }, {});

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h5">{listing?.title}</Typography>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            {listing?.location}
          </Typography>
          <Typography variant="h4" color="primary" gutterBottom>
            {currencySymbol}{listing?.price}
          </Typography>
          <Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
            {listing?.description}
          </Typography>
          
          {availableAmenities.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="h6" gutterBottom>
                Available Amenities
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {availableAmenities.map((amenity, index) => (
                  <Chip 
                    key={index} 
                    label={amenity.name} 
                    color="primary" 
                    variant="outlined"
                  />
                ))}
              </Box>
            </Box>
          )}
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            All Amenities
          </Typography>
          {Object.entries(groupedAmenities).map(([category, items]) => (
            <Box key={category} sx={{ mb: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1, fontWeight: 'bold' }}>
                {category}
              </Typography>
              <List dense>
                {items.map(a => (
                  <ListItem key={a.id}>
                    <ListItemIcon>
                      {category === 'Not included' ? (
                        <Cancel color="error" />
                      ) : availableIds.has(a.id) ? (
                        <CheckCircle color="success" />
                      ) : (
                        <Cancel color="disabled" />
                      )}
                    </ListItemIcon>
                    <ListItemText
                      primary={a.name.replace(/^Unavailable: /, '')}
                      sx={{ 
                        color: category === 'Not included' ? 'error.main' : 
                               availableIds.has(a.id) ? 'inherit' : 'text.disabled' 
                      }}
                    />
                  </ListItem>
                ))}
              </List>
              <Divider />
            </Box>
          ))}
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Button
            href={listing?.airbnb_link}
            target="_blank"
            rel="noopener noreferrer"
            variant="contained"
            size="large"
            endIcon={<OpenInNew />}
          >
            View on Airbnb
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
}

export default DetailsModal; 